/** 이 파일만 쓰는 짧은 기다림. 드라이버 어디에나 있는 것과 같다. */
function sleep(ms: number): Promise<void> {
  return new Promise((done) => { setTimeout(done, ms); });
}

/** 달력 칸을 그리는 레이아웃. 클래스 이름에 그대로 박혀 있다. */
const CALENDAR = '[class*="CalendarLayout" i]';
const OPEN_MS = 6000;
const POLL_MS = 150;

function press(node: Element): void {
  for (const type of ['mousedown', 'mouseup', 'click'] as const) {
    node.dispatchEvent(new MouseEvent(type, { bubbles: true, cancelable: true }));
  }
}

/** 지금 떠 있는 창 안의 표 이름들. 창이 열렸는지 재는 데 쓴다. */
function gridsOnScreen(): string[] {
  return Array.from(document.querySelectorAll('[data-el-grid]'))
    .map((node) => node.getAttribute('data-el-grid') ?? '')
    .filter(Boolean);
}

/**
 * 달력 화면에서 **그 날을 연다.**
 *
 * 스케쥴관리(`/w_tb_e501`)는 헤더 입력칸이 하나도 없고, 날짜 칸을 누르면 그날치
 * `스케쥴 등록` 창이 열린다. 그 창 안에서만 행을 더하고 저장할 수 있다 — 즉
 * **이 클릭이 없으면 그 화면에는 아무것도 쓸 수 없다.**
 *
 * 날짜는 여덟 자리(`20260827`)로 받는다. 달력에는 앞뒤 달의 날짜도 함께 깔리므로
 * (8월 달력의 첫 칸이 7월 26일이다) **그 달에 속한 칸**만 고른다 — 뒤에서부터 찾으면
 * 앞 달의 같은 날짜를 집는다.
 */
export async function openCalendarDay(date: string): Promise<void> {
  const day = String(Number(date.slice(6, 8)));
  const cells = Array.from(document.querySelectorAll(`${CALENDAR} [class*="day" i]`));
  if (!cells.length) throw new Error('달력 칸을 찾지 못했다');

  // 1일이 나오기 전의 칸은 앞 달이다. 그 뒤에서 같은 날짜를 찾는다.
  const startsMonth = cells.findIndex((cell) => (cell.textContent ?? '').trim().startsWith('1'));
  const from = startsMonth < 0 ? 0 : startsMonth;
  const target = cells.slice(from).find((cell) => {
    const text = (cell.textContent ?? '').trim();
    return new RegExp(`^${day}(?!\d)`).test(text);
  });
  if (!target) throw new Error(`달력에서 ${date} 칸을 찾지 못했다`);

  const before = gridsOnScreen().length;
  press(target);

  // 창이 뜨면 표가 하나 늘어난다. 화면마다 이름이 달라 개수로 잰다.
  const deadline = Date.now() + OPEN_MS;
  while (gridsOnScreen().length <= before && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
  }
  if (gridsOnScreen().length <= before) {
    throw new Error(`${date} 칸을 눌렀지만 창이 열리지 않았다`);
  }
}

/**
 * 날짜 칸이 여는 **작은 달력**. 화면 뒤의 큰 달력(`CalendarLayout`)과 다르다.
 *
 * 앱 전체에 하나뿐이고(`ModalCalendar`, `id="calendar"`) 닫혀 있을 때도 DOM 에 남아
 * 투명도만 0 이 된다. 그래서 「있는가」가 아니라 **머리에 달이 적혀 있고 투명하지 않은가**로
 * 열렸는지 잰다.
 */
const DATE_POPUP = '[class*="DateTextBox-module__calendar"]';
const MONTH_LABEL = /^(\d{4})년\s*(\d{1,2})월$/;
const PICK_MS = 4000;
// 달을 넘길 수 있는 한도. 두 해를 넘기는 옮기기는 사람도 달력으로 하지 않는다.
const MAX_PAGES = 24;

function monthLabel(popup: Element): HTMLElement | null {
  return Array.from(popup.querySelectorAll<HTMLElement>('*'))
    .find((node) => node.children.length === 0
      && MONTH_LABEL.test((node.textContent ?? '').replace(/\s+/g, ' ').trim())) ?? null;
}

function openDatePopup(): HTMLElement | null {
  return Array.from(document.querySelectorAll<HTMLElement>(DATE_POPUP))
    .find((node) => node.style.opacity !== '0' && monthLabel(node) !== null) ?? null;
}

/** 머리에 적힌 달. `2026년 9월` → `202609`. */
function shownMonth(popup: Element): string {
  const match = MONTH_LABEL.exec((monthLabel(popup)?.textContent ?? '').replace(/\s+/g, ' ').trim());
  return match ? `${match[1]}${(match[2] ?? '').padStart(2, '0')}` : '';
}

/** 달력의 날 칸들(머리 밖, 숫자 하나짜리). 앞뒤 달의 날도 들어 있다. */
function dayCells(popup: Element): HTMLElement[] {
  return Array.from(popup.querySelectorAll<HTMLElement>('*'))
    .filter((node) => node.children.length === 0
      && /^\d{1,2}$/.test((node.textContent ?? '').trim())
      && !node.closest('[class*="header" i]'));
}

function isOther(node: Element): boolean {
  return /other/i.test(String(node.className ?? ''));
}

/**
 * 날 칸이 **그 달의 모양**인가. 달력은 일요일부터 깔리므로 1일 앞에 앞 달 날이 그 요일 수만큼
 * 오고, 그 달 날은 그 달 날수만큼이다. 머리 글자와 따로 바뀌므로 칸으로 따로 잰다.
 */
function laidOutFor(popup: Element, month: string): boolean {
  const year = Number(month.slice(0, 4));
  const index = Number(month.slice(4, 6)) - 1;
  const cells = dayCells(popup);
  const lead = cells.findIndex((node) => !isOther(node));
  const own = cells.filter((node) => !isOther(node)).length;
  return lead === new Date(year, index, 1).getDay() && own === new Date(year, index + 1, 0).getDate();
}

function monthsBetween(from: string, to: string): number {
  const count = (value: string) => Number(value.slice(0, 4)) * 12 + Number(value.slice(4, 6)) - 1;
  return count(to) - count(from);
}

/**
 * **날짜 칸의 달력을 열어 그 날을 누른다.** 글자를 치지 않는다.
 *
 * 점검계획의 점검일자는 글자를 치고 Enter/Tab 으로는 바뀌지 않았다(2026-09-30 개발 테넌트,
 * 원인 미확인). 사람은 칸을 눌러 뜬 달력에서 고르고, 그 길은 확실했다.
 *
 * - 달은 머리의 ‹ › 로 넘긴다. 가운데 글자(`2026년 9월`)가 목표 달이 될 때까지.
 * - 날은 **`mousedown` 에 반응한다**(`ModalCalendar`). click 을 보내면 아무 일도 없다.
 * - 앞뒤 달의 날도 함께 깔린다(`other`). 그 칸은 고르지 않는다 — 같은 숫자가 둘이다.
 *
 * 누른 뒤에 무엇이 일어나는지는 부르는 쪽이 본다. 점검일자는 누르는 순간 서버에 반영된다.
 */
export async function pickDate(input: HTMLElement, date: string): Promise<void> {
  if (!/^\d{8}$/.test(date)) throw new Error(`날짜는 여덟 자리여야 합니다: ${date}`);
  input.focus();
  input.click();
  let deadline = Date.now() + PICK_MS;
  let popup = openDatePopup();
  while (!popup && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
    popup = openDatePopup();
  }
  if (!popup) throw new Error('날짜 칸을 눌렀지만 달력이 열리지 않았습니다.');

  const target = date.slice(0, 6);
  for (let page = 0; page < MAX_PAGES && shownMonth(popup) !== target; page += 1) {
    const now = shownMonth(popup);
    const label = monthLabel(popup);
    const step = monthsBetween(now, target) > 0 ? label?.nextElementSibling : label?.previousElementSibling;
    if (!now || !step) throw new Error('달력의 달을 넘기는 단추를 찾지 못했습니다.');
    step.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));
    deadline = Date.now() + PICK_MS;
    while (shownMonth(popup) === now && Date.now() < deadline) {
      // eslint-disable-next-line no-await-in-loop
      await sleep(POLL_MS);
    }
  }
  if (shownMonth(popup) !== target) {
    throw new Error(`달력을 ${Number(date.slice(4, 6))}월로 넘기지 못했습니다.`);
  }
  // **머리 글자가 먼저 바뀌고 날 칸은 한 박자 늦게 바뀐다.** 머리만 보고 누르면 앞 달의 칸을
  // 누른다 — 10월 2일 계획을 11월 5일로 옮기라 했더니 10월 5일로 옮겨졌다(2026-09-30 개발
  // 테넌트, 머리 `11월` 인데 칸은 아직 10월이었다). 칸이 그 달 모양이 될 때까지 기다린다.
  deadline = Date.now() + PICK_MS;
  while (!laidOutFor(popup, target) && Date.now() < deadline) {
    // eslint-disable-next-line no-await-in-loop
    await sleep(POLL_MS);
  }
  if (!laidOutFor(popup, target)) {
    throw new Error(`달력이 ${Number(date.slice(4, 6))}월 날짜로 바뀌지 않았습니다. 아무것도 누르지 않았습니다.`);
  }

  const wanted = String(Number(date.slice(6, 8)));
  const day = dayCells(popup).find((node) => (node.textContent ?? '').trim() === wanted && !isOther(node));
  if (!day) throw new Error(`달력에서 ${Number(date.slice(4, 6))}월 ${wanted}일을 찾지 못했습니다.`);
  day.dispatchEvent(new MouseEvent('mousedown', { bubbles: true, cancelable: true }));
}
